import { useEffect } from 'react'
import { PlanArtifact, artifactMeta, visibleArtifacts, type ArtifactCtx, type ArtifactId } from './artifacts'
import { Icon } from './Icon'
import { useOverlayHistory } from '../useOverlayHistory'

// ArtifactDock — the launcher bar pinned to the bottom of the console. One tile
// per artifact this plan can have, each showing its live state ("draft · $4,188")
// so the dock doubles as the plan's status strip. Clicking a tile opens it
// fullscreen (ArtifactFrame). An artifact with no content yet still shows —
// dimmed — so the operator can see what the plan is *missing*, not just what it has.

export function ArtifactDock({
  ctx,
  openId,
  onOpen,
}: {
  ctx: ArtifactCtx
  openId: ArtifactId | null
  onOpen: (id: ArtifactId | null) => void
}) {
  // Nine equally-weighted tools read as a settings dump, and "which of these
  // does this moment need" costs a scan of all of them. The dock is ordered by
  // the job's current stage instead — the tools this stage is about lead, the
  // rest follow, quieter. Nothing is hidden and nothing moves further away:
  // every artifact stays exactly one click from a cold load.
  const items = visibleArtifacts(ctx)
  const stage = ctx.readiness?.job_stage
  const serves = (a: (typeof items)[number]) => Boolean(stage && a.stages?.includes(stage as never))
  const ordered = stage
    ? [...items].sort((a, b) => Number(serves(b)) - Number(serves(a)))
    : items
  // Exactly ONE tile carries the accent. Several stages are served by two or
  // three artifacts, and lighting them all up turns a recommendation into what
  // looks like a multi-select — the signal is "start here", which only works if
  // it points at one thing. The rest stay full-strength and simply lead the row.
  const leadId = ordered.find(serves)?.id
  return (
    <div className="dock" role="toolbar" aria-label="Plan artifacts">
      {ordered.map((a) => {
        const ready = a.ready ? a.ready(ctx) : true
        const needs = a.attention ? a.attention(ctx) : false
        const now = a.id === leadId
        return (
          <button
            key={a.id}
            className={`dock-tile${openId === a.id ? ' open' : ''}${ready ? '' : ' empty'}${needs ? ' attention' : ''}${serves(a) ? ' serves' : ''}${now ? ' now' : ''}`}
            aria-pressed={openId === a.id}
            title={now ? `${a.label} — ${a.summary(ctx)} · for this stage` : `${a.label} — ${a.summary(ctx)}`}
            onClick={() => onOpen(openId === a.id ? null : a.id)}
          >
            <span className="dock-glyph">
              <Icon name={a.icon} size={16} />
            </span>
            <span className="dock-label">{a.label}</span>
            {/* Only state that WANTS something shows on the tile; the rest is in
                the tooltip, so the dock stays a quiet launcher rather than a
                second dashboard competing with the conversation. */}
            {needs ? <span className="dock-flag">{a.summary(ctx)}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

// ArtifactFrame — the fullscreen experience a dock tile opens into. Mirrors the
// Maximizable idiom already used elsewhere (body class + Esc to exit) so the
// chrome-free behaviour is identical wherever the operator meets it.
export function ArtifactFrame({ id, ctx, onClose }: { id: ArtifactId; ctx: ArtifactCtx; onClose: () => void }) {
  const meta = artifactMeta(id)
  // Back closes the artifact instead of leaving the view. Every exit — Back,
  // Esc, the control — goes through the same dismiss so history stays balanced.
  const dismiss = useOverlayHistory(true, onClose)
  useEffect(() => {
    document.body.classList.add('panel-maximized')
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.classList.remove('panel-maximized')
    }
  }, [dismiss])

  if (!meta) return null
  return (
    <div className="artifact-frame" role="dialog" aria-modal="true" aria-label={meta.label}>
      <header className="artifact-frame-head">
        {/* Top-left back: the same thing the ✕ does, where people already look
            to leave a fullscreen view. */}
        <button className="artifact-back" onClick={dismiss} title="Back (Esc)">
          <span aria-hidden>←</span> Back
        </button>
        <span className="artifact-frame-glyph">
          <Icon name={meta.icon} size={20} />
        </span>
        <h2>{meta.label}</h2>
        <span className="artifact-frame-sum">{meta.summary(ctx)}</span>
        <button className="artifact-frame-close" onClick={dismiss} title="Close (Esc)">
          ✕
        </button>
      </header>
      <div className="artifact-frame-body">
        <PlanArtifact id={id} />
      </div>
    </div>
  )
}
